/**
 * Gomila search history for the search drawer (sections/search-drawer.liquid).
 *
 * Remembers, in this browser only, the visitor's last searches and the items (products, articles, collections,
 * pages) they opened from the search results, and shows them while the search box is empty. Nothing is sent anywhere.
 * Storage is skipped when the visitor's privacy settings deny it, and a blocked storage never throws.
 */
(() => {
  const STORAGE_KEY = 'gomila:search-history';
  const MAX_QUERIES = 8;
  const MAX_ITEMS = 8;
  const MAX_QUERY_LENGTH = 80;
  const CONSENT_TIMEOUT = 4000;

  const empty = () => ({ queries: [], items: [] });

  const isSafePath = (value) => typeof value === 'string' && /^\/(?!\/)/.test(value);
  const isSafeImage = (value) => typeof value === 'string' && /^(https:)?\/\/[^/]+\//.test(value);

  const store = {
    read() {
      try {
        const data = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        if (!data || typeof data !== 'object') return empty();

        const queries = (Array.isArray(data.queries) ? data.queries : [])
          .filter((q) => typeof q === 'string' && q.trim() !== '')
          .slice(0, MAX_QUERIES);

        const items = (Array.isArray(data.items) ? data.items : [])
          .filter((item) => item && typeof item.title === 'string' && isSafePath(item.url))
          .slice(0, MAX_ITEMS);

        return { queries, items };
      } catch (e) {
        return empty();
      }
    },

    write(data) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        // Storage blocked or full: searching keeps working, just without history.
      }
    },

    clear() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    }
  };

  /** Only an explicit "denied" from Shopify's Customer Privacy API blocks storage. */
  const readConsent = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
      return Promise.resolve(Boolean(privacy.preferencesProcessingAllowed()));
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(true), CONSENT_TIMEOUT);

      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          window.clearTimeout(timer);
          const loaded = window.Shopify.customerPrivacy;
          resolve(error || !loaded || typeof loaded.preferencesProcessingAllowed !== 'function' ? true : Boolean(loaded.preferencesProcessingAllowed()));
        });
      } catch (e) {
        window.clearTimeout(timer);
        resolve(true);
      }
    });
  };

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_QUERY_LENGTH);

  class SearchHistory {
    constructor(drawer) {
      this.drawer = drawer;
      this.form = drawer.querySelector('form');
      this.input = drawer.querySelector('input[name="q"]');
      this.container = drawer.querySelector('[data-search-history]');
      if (!this.form || !this.input || !this.container) return;

      this.queriesBlock = this.container.querySelector('[data-history-queries]');
      this.queryList = this.container.querySelector('[data-history-query-list]');
      this.itemsBlock = this.container.querySelector('[data-history-items]');
      this.itemList = this.container.querySelector('[data-history-item-list]');
      this.allowed = true;

      this.refreshConsent();
      document.addEventListener('visitorConsentCollected', () => this.refreshConsent());

      this.form.addEventListener('submit', () => this.recordQuery(this.input.value));
      this.form.addEventListener('click', (event) => this.onClick(event));
      this.input.addEventListener('focus', () => this.render());
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY) this.render();
      });

      this.container.querySelector('[data-history-clear]').addEventListener('click', () => {
        store.clear();
        this.render();
      });

      this.render();
    }

    async refreshConsent() {
      this.allowed = await readConsent();
      if (!this.allowed) store.clear();
      this.render();
    }

    recordQuery(raw) {
      const query = normalize(raw);
      if (!this.allowed || query.length < 2) return;

      const data = store.read();
      data.queries = [query, ...data.queries.filter((q) => q.toLowerCase() !== query.toLowerCase())].slice(0, MAX_QUERIES);
      store.write(data);
      this.render();
    }

    recordItem(link) {
      if (!this.allowed) return;

      const url = link.getAttribute('href') || '';
      const path = isSafePath(url) ? url.split('#')[0] : '';
      const label = link.querySelector('.reversed-link');
      const title = normalize((label || link).textContent);
      if (!path || !title) return;

      const image = link.querySelector('img');
      const imageUrl = image ? image.currentSrc || image.getAttribute('src') : '';
      const item = { title, url: path };
      if (isSafeImage(imageUrl)) item.image = imageUrl;

      const data = store.read();
      data.items = [item, ...data.items.filter((existing) => existing.url !== path)].slice(0, MAX_ITEMS);
      store.write(data);
      this.render();
    }

    onClick(event) {
      const pill = event.target.closest('.search-popular-term');
      if (pill) {
        this.recordQuery(pill.getAttribute('data-search-term'));
        return;
      }

      const link = event.target.closest('[slot="results"] a[href]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      if (/^\/([a-z]{2}(-[a-z]{2})?\/)?search(\?|$)/i.test(href)) {
        // A "suggestion" link: the search term itself
        this.recordQuery(link.textContent);
        return;
      }

      this.recordQuery(this.input.value);
      this.recordItem(link);
    }

    render() {
      const data = this.allowed ? store.read() : empty();

      this.queryList.replaceChildren(
        ...data.queries.map((query) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'search-popular-term';
          button.setAttribute('data-search-term', query);

          const label = document.createElement('span');
          label.textContent = query;
          button.append(label);
          return button;
        })
      );

      this.itemList.replaceChildren(
        ...data.items.map((item) => {
          const link = document.createElement('a');
          link.className = 'predictive-search-result group';
          link.href = item.url;

          if (item.image && isSafeImage(item.image)) {
            const image = document.createElement('img');
            image.src = item.image;
            image.alt = '';
            image.width = 96;
            image.loading = 'lazy';
            link.append(image);
          }

          const text = document.createElement('div');
          text.className = 'v-stack gap-1 sm:gap-1.5';
          const bold = document.createElement('span');
          bold.className = 'bold';
          const reversed = document.createElement('span');
          reversed.className = 'reversed-link';
          reversed.textContent = item.title;
          bold.append(reversed);
          text.append(bold);
          link.append(text);
          return link;
        })
      );

      this.queriesBlock.hidden = data.queries.length === 0;
      this.itemsBlock.hidden = data.items.length === 0;
      this.container.hidden = data.queries.length === 0 && data.items.length === 0;
    }
  }

  const init = () => document.querySelectorAll('search-drawer').forEach((drawer) => {
    if (!drawer.__searchHistory) drawer.__searchHistory = new SearchHistory(drawer);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section
  document.addEventListener('shopify:section:load', init);
})();
